import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import logo from "../../assets/logo.png";
import {
  signUp,
  logIn,
  requestPasswordReset,
  requestOtpForMail,
  verifyOtpForMail,
} from "../../api/ticketsPlatform/auth";
import { useUserStore } from "../../store/userStore";
import Navbar from "../../components/tickets/navBar";
import Footer from "../../components/tickets/footer";

export default function AuthPage() {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect"); // e.g. "checkout"

  const setAuth = useUserStore((s) => s.setAuth);

  /* ── LOGIN STATE ── */
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginErrors, setLoginErrors] = useState({});
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginApiError, setLoginApiError] = useState(null);

  /* ── SIGNUP STATE ── */
  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    isTermsAccepted: false,
    isMarketingConsentAfrodise: false,
    isMarketingConsentPartners: false,
  });
  const [signupErrors, setSignupErrors] = useState({});
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupApiError, setSignupApiError] = useState(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  /* ── FORGOT PASSWORD STATE ── */
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState(null);
  const [forgotSuccess, setForgotSuccess] = useState(false);

  /* ── OTP / MAIL VERIFY STATE ── */
  const [signupOtpSent, setSignupOtpSent] = useState(false);
  const [signupOtpVerified, setSignupOtpVerified] = useState(false);
  const [signupOtpValues, setSignupOtpValues] = useState([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [signupOtpLoading, setSignupOtpLoading] = useState(false);
  const [signupOtpError, setSignupOtpError] = useState(null);
  const [signupOtpToken, setSignupOtpToken] = useState(null);
  const [signupResendCountdown, setSignupResendCountdown] = useState(0);

  /* ── HELPERS ── */
  const handleRedirect = () => {
    if (redirectTo === "checkout") {
      navigate("/tickets-store/checkout");
    } else {
      navigate("/tickets-store/user");
    }
  };

  /* ── LOGIN ── */
  const validateLogin = () => {
    const e = {};
    if (!loginForm.email.trim() || !/\S+@\S+\.\S+/.test(loginForm.email))
      e.email = "Email inválido";
    if (!loginForm.password.trim()) e.password = "Requerido";
    return e;
  };

  const handleLogin = async () => {
    const e = validateLogin();
    if (Object.keys(e).length > 0) {
      setLoginErrors(e);
      return;
    }

    try {
      setLoginLoading(true);
      setLoginApiError(null);
      const data = await logIn({
        email: loginForm.email,
        password: loginForm.password,
      });
      setAuth(data.user, data.token);
      handleRedirect();
    } catch (err) {
      setLoginApiError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Email o contraseña incorrectos.",
      );
    } finally {
      setLoginLoading(false);
    }
  };

  /* ── SIGNUP ── */
  const validateSignup = () => {
    const e = {};
    if (!signupForm.firstName.trim()) e.firstName = "Requerido";
    if (!signupForm.lastName.trim()) e.lastName = "Requerido";
    if (!signupForm.email.trim() || !/\S+@\S+\.\S+/.test(signupForm.email))
      e.email = "Email inválido";
    if (!signupOtpVerified)
      e.email = "Debes verificar tu email antes de continuar";
    if (!signupForm.password || signupForm.password.length < 8)
      e.password = "Mínimo 8 caracteres";
    if (signupForm.password !== signupForm.confirmPassword)
      e.confirmPassword = "Las contraseñas no coinciden";
    if (!signupForm.isTermsAccepted)
      e.isTermsAccepted = "Debes aceptar los términos para continuar";
    return e;
  };

  const handleSignup = async () => {
    const e = validateSignup();
    if (Object.keys(e).length > 0) {
      setSignupErrors(e);
      return;
    }

    try {
      setSignupLoading(true);
      setSignupApiError(null);
      await signUp({
        first_name: signupForm.firstName,
        last_name: signupForm.lastName,
        email: signupForm.email,
        otp_verification_token: signupOtpToken,
        password: signupForm.password,
        is_terms_accepted: signupForm.isTermsAccepted,
        is_marketing_consent_afrodise: signupForm.isMarketingConsentAfrodise,
        is_marketing_consent_partners: signupForm.isMarketingConsentPartners,
      });
      setSignupSuccess(true);
    } catch (err) {
      setSignupApiError(
        err?.response?.data?.detail ||
          err?.response?.data?.email?.[0] ||
          err?.response?.data?.message ||
          "Error al crear la cuenta. Inténtalo de nuevo.",
      );
    } finally {
      setSignupLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim() || !/\S+@\S+\.\S+/.test(forgotEmail)) {
      setForgotError("Email inválido");
      return;
    }
    try {
      setForgotLoading(true);
      setForgotError(null);
      await requestPasswordReset(forgotEmail);
      setForgotSuccess(true);
    } catch (err) {
      setForgotError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Algo salió mal. Inténtalo de nuevo.",
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const sendSignupOtp = async (email) => {
    setSignupOtpLoading(true);
    setSignupOtpError(null);
    try {
      await requestOtpForMail(email, "signup");
      setSignupOtpSent(true);
      startSignupResendCountdown();
    } catch (err) {
      console.log("--", err.response);
      setSignupOtpError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Error al enviar el código. Inténtalo de nuevo.",
      );
    } finally {
      setSignupOtpLoading(false);
    }
  };

  const verifySignupOtp = async () => {
    const code = signupOtpValues.join("");
    if (code.length !== 6) return;
    setSignupOtpLoading(true);
    setSignupOtpError(null);
    try {
      const data = await verifyOtpForMail(signupForm.email, code, "signup");
      setSignupOtpVerified(true);
      setSignupOtpToken(data.otp_verification_token);
    } catch (err) {
      setSignupOtpError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Código incorrecto. Inténtalo de nuevo.",
      );
      setSignupOtpValues(["", "", "", "", "", ""]);
    } finally {
      setSignupOtpLoading(false);
    }
  };

  const startSignupResendCountdown = () => {
    setSignupResendCountdown(60);
    const interval = setInterval(() => {
      setSignupResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSignupOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newValues = [...signupOtpValues];
    newValues[index] = value.slice(-1);
    setSignupOtpValues(newValues);
    setSignupOtpError(null);
    if (value && index < 5) {
      document.getElementById(`signup-otp-${index + 1}`)?.focus();
    }
  };

  const handleSignupOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !signupOtpValues[index] && index > 0) {
      document.getElementById(`signup-otp-${index - 1}`)?.focus();
    }
  };

  const handleSignupOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    const newValues = [...signupOtpValues];
    pasted.split("").forEach((char, i) => {
      newValues[i] = char;
    });
    setSignupOtpValues(newValues);
    document
      .getElementById(`signup-otp-${Math.min(pasted.length, 5)}`)
      ?.focus();
  };

  return (
    <div className="bg-[#021610] text-white min-h-screen flex flex-col">
      {/* NAVBAR */}
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 pt-20 pb-16">
        <div className="w-full max-w-md">
          {/* BACK */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition mb-8"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Volver
          </button>

          {/* TAB SWITCHER */}
          <div className="flex bg-[#031c14] border border-green-900/30 rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode("login")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium tracking-widest uppercase transition
                ${
                  mode === "login"
                    ? "bg-green-500 text-black"
                    : "text-gray-400 hover:text-white"
                }`}
            >
              Iniciar sesión
            </button>
            <button
              onClick={() => setMode("signup")}
              className={`flex-1 py-2 rounded-lg text-sm font-medium tracking-widest uppercase transition
                ${
                  mode === "signup"
                    ? "bg-green-500 text-black"
                    : "text-gray-400 hover:text-white"
                }`}
            >
              Crear cuenta
            </button>
          </div>

          {/* ── LOGIN FORM ── */}
          {mode === "login" && (
            <div className="space-y-5">
              <div>
                <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
                  Bienvenido de nuevo
                </p>
                <h1 className="text-2xl font-bold mt-1">Iniciar sesión</h1>
              </div>

              <Field
                label="Email"
                type="email"
                value={loginForm.email}
                onChange={(v) => {
                  setLoginForm((f) => ({ ...f, email: v }));
                  setLoginErrors((e) => ({ ...e, email: undefined }));
                  setLoginApiError(null);
                }}
                error={loginErrors.email}
                placeholder="tu@email.com"
              />

              <Field
                label="Contraseña"
                type="password"
                value={loginForm.password}
                onChange={(v) => {
                  setLoginForm((f) => ({ ...f, password: v }));
                  setLoginErrors((e) => ({ ...e, password: undefined }));
                  setLoginApiError(null);
                }}
                error={loginErrors.password}
                placeholder="••••••••"
              />

              <div className="flex justify-end">
                <button
                  onClick={() => setShowForgot(true)}
                  className="text-xs text-green-400/60 hover:text-green-400 transition"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              {loginApiError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                  <p className="text-sm text-red-400">{loginApiError}</p>
                </div>
              )}

              {redirectTo === "checkout" && (
                <div className="bg-green-900/20 border border-green-700/30 rounded-xl px-4 py-3">
                  <p className="text-xs text-green-400">
                    Inicia sesión para completar tu compra.
                  </p>
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={loginLoading}
                className={`w-full py-4 rounded-xl font-bold tracking-widest uppercase text-sm transition
                  ${
                    loginLoading
                      ? "bg-green-700 text-black/50 cursor-not-allowed"
                      : "bg-green-500 text-black hover:bg-green-400"
                  }`}
              >
                {loginLoading ? "Iniciando sesión..." : "Entrar →"}
              </button>

              <p className="text-center text-xs text-gray-500">
                ¿No tienes cuenta?{" "}
                <button
                  onClick={() => setMode("signup")}
                  className="text-green-400 hover:text-green-300 transition"
                >
                  Crear cuenta
                </button>
              </p>
            </div>
          )}

          {/* FORGOT PASSWORD OVERLAY */}
          {showForgot && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4">
              <div className="w-full max-w-sm bg-[#02160e] border border-green-900/40 rounded-2xl p-6 space-y-5">
                {!forgotSuccess ? (
                  <>
                    <div>
                      <p className="text-[10px] tracking-widest text-green-400/70 uppercase mb-1">
                        Recuperar acceso
                      </p>
                      <h2 className="text-xl font-bold">
                        ¿Olvidaste tu contraseña?
                      </h2>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                        Introduce tu email y te enviaremos un enlace para
                        restablecer tu contraseña.
                      </p>
                    </div>

                    <Field
                      label="Email"
                      type="email"
                      value={forgotEmail}
                      onChange={(v) => {
                        setForgotEmail(v);
                        setForgotError(null);
                      }}
                      error={forgotError}
                      placeholder="tu@email.com"
                    />

                    <button
                      onClick={handleForgotPassword}
                      disabled={forgotLoading}
                      className={`w-full py-3 rounded-xl font-bold tracking-widest uppercase text-sm transition
              ${
                forgotLoading
                  ? "bg-green-700 text-black/50 cursor-not-allowed"
                  : "bg-green-500 text-black hover:bg-green-400"
              }`}
                    >
                      {forgotLoading ? "Enviando..." : "Enviar enlace →"}
                    </button>

                    <button
                      onClick={() => {
                        setShowForgot(false);
                        setForgotEmail("");
                        setForgotError(null);
                      }}
                      className="w-full py-2 text-xs text-gray-500 hover:text-white tracking-widest uppercase transition"
                    >
                      Cancelar
                    </button>
                  </>
                ) : (
                  /* SUCCESS */
                  <div className="text-center space-y-4">
                    <div className="w-14 h-14 mx-auto rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                      <span className="text-2xl">✉️</span>
                    </div>
                    <div>
                      <p className="text-[10px] tracking-widest text-green-400 uppercase mb-2">
                        Email enviado
                      </p>
                      <h2 className="text-xl font-bold">Revisa tu bandeja</h2>
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                        Si el email existe en nuestra plataforma, recibirás un
                        enlace para restablecer tu contraseña.
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowForgot(false);
                        setForgotEmail("");
                        setForgotSuccess(false);
                      }}
                      className="w-full py-3 rounded-xl border border-green-500 text-green-400 font-bold tracking-widests uppercase text-sm hover:bg-green-500 hover:text-black transition"
                    >
                      Volver al inicio de sesión
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── SIGNUP FORM ── */}
          {mode === "signup" && !signupSuccess && (
            <div className="space-y-5">
              <div>
                <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
                  Únete a Afrodise
                </p>
                <h1 className="text-2xl font-bold mt-1">Crear cuenta</h1>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Nombre"
                  value={signupForm.firstName}
                  onChange={(v) => {
                    setSignupForm((f) => ({ ...f, firstName: v }));
                    setSignupErrors((e) => ({ ...e, firstName: undefined }));
                  }}
                  error={signupErrors.firstName}
                  placeholder="Tu nombre"
                />
                <Field
                  label="Apellidos"
                  value={signupForm.lastName}
                  onChange={(v) => {
                    setSignupForm((f) => ({ ...f, lastName: v }));
                    setSignupErrors((e) => ({ ...e, lastName: undefined }));
                  }}
                  error={signupErrors.lastName}
                  placeholder="Tus apellidos"
                />
              </div>

              {/* EMAIL + OTP */}
              <div className="space-y-3">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] tracking-widest text-gray-400 uppercase">
                    Email
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={signupForm.email}
                      disabled={signupOtpVerified}
                      onChange={(e) => {
                        setSignupForm((f) => ({ ...f, email: e.target.value }));
                        setSignupErrors((e) => ({ ...e, email: undefined }));
                        setSignupApiError(null);
                        setSignupOtpSent(false);
                        setSignupOtpVerified(false);
                        setSignupOtpValues(["", "", "", "", "", ""]);
                        setSignupOtpError(null);
                        setSignupOtpToken(null);
                        setSignupResendCountdown(0);
                      }}
                      placeholder="tu@email.com"
                      className={`flex-1 bg-[#031c14] border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition
          ${
            signupErrors.email
              ? "border-red-500/60"
              : signupOtpVerified
                ? "border-green-500/60 opacity-70"
                : "border-green-900/40 focus:border-green-500/60"
          }`}
                    />
                    {!signupOtpVerified && (
                      <button
                        onClick={() => {
                          if (
                            !signupForm.email ||
                            !/\S+@\S+\.\S+/.test(signupForm.email)
                          ) {
                            setSignupErrors((e) => ({
                              ...e,
                              email: "Email inválido",
                            }));
                            return;
                          }
                          sendSignupOtp(signupForm.email);
                        }}
                        disabled={signupOtpLoading || signupResendCountdown > 0}
                        className={`flex-shrink-0 px-4 py-3 rounded-xl text-xs font-bold tracking-widest uppercase transition
            ${
              signupOtpLoading || signupResendCountdown > 0
                ? "bg-green-900/30 text-green-900 cursor-not-allowed"
                : "bg-green-500 text-black hover:bg-green-400"
            }`}
                      >
                        {signupOtpLoading
                          ? "..."
                          : signupOtpSent
                            ? signupResendCountdown > 0
                              ? `${signupResendCountdown}s`
                              : "Reenviar"
                            : "Verificar"}
                      </button>
                    )}
                    {signupOtpVerified && (
                      <div className="flex-shrink-0 flex items-center gap-2">
                        <div className="flex items-center gap-1.5 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/20">
                          <span className="text-green-400 text-sm">✓</span>
                          <span className="text-green-400 text-xs tracking-widest uppercase">
                            Verificado
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            setSignupOtpSent(false);
                            setSignupOtpVerified(false);
                            setSignupOtpValues(["", "", "", "", "", ""]);
                            setSignupOtpError(null);
                            setSignupOtpToken(null);
                          }}
                          className="px-3 py-3 rounded-xl border border-green-900/40 text-gray-400 text-xs tracking-widest uppercase hover:border-green-700/50 hover:text-white transition"
                        >
                          Cambiar
                        </button>
                      </div>
                    )}
                  </div>
                  {signupErrors.email && (
                    <p className="text-[10px] text-red-400 tracking-wide">
                      {signupErrors.email}
                    </p>
                  )}
                  {signupOtpError && !signupOtpSent && (
                    <p className="text-[10px] text-red-400 tracking-wide">
                      {signupOtpError}
                    </p>
                  )}
                </div>

                {/* OTP BOXES */}
                {signupOtpSent && !signupOtpVerified && (
                  <div className="bg-[#031c14] border border-green-900/30 rounded-xl p-4 space-y-4">
                    <div>
                      <p className="text-sm text-white font-medium mb-1">
                        Introduce el código de verificación
                      </p>
                      <p className="text-xs text-gray-500">
                        Hemos enviado un código de 6 dígitos a{" "}
                        <span className="text-green-400">
                          {signupForm.email}
                        </span>
                      </p>
                    </div>
                    <div className="flex gap-2 justify-between">
                      {signupOtpValues.map((val, index) => (
                        <input
                          key={index}
                          id={`signup-otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={val}
                          onChange={(e) =>
                            handleSignupOtpChange(index, e.target.value)
                          }
                          onKeyDown={(e) => handleSignupOtpKeyDown(index, e)}
                          onPaste={
                            index === 0 ? handleSignupOtpPaste : undefined
                          }
                          className={`w-full aspect-square max-w-[48px] text-center text-lg font-bold bg-[#021610] border rounded-xl text-white outline-none transition
              ${
                signupOtpError
                  ? "border-red-500/60"
                  : val
                    ? "border-green-500/60 text-green-400"
                    : "border-green-900/40 focus:border-green-500/60"
              }`}
                        />
                      ))}
                    </div>
                    {signupOtpError && (
                      <p className="text-[10px] text-red-400 tracking-wide">
                        {signupOtpError}
                      </p>
                    )}
                    <button
                      onClick={verifySignupOtp}
                      disabled={
                        signupOtpValues.join("").length !== 6 ||
                        signupOtpLoading
                      }
                      className={`w-full py-3 rounded-xl font-bold tracking-widest uppercase text-sm transition
          ${
            signupOtpValues.join("").length !== 6 || signupOtpLoading
              ? "bg-green-900/30 text-green-900 cursor-not-allowed"
              : "bg-green-500 text-black hover:bg-green-400"
          }`}
                    >
                      {signupOtpLoading
                        ? "Verificando..."
                        : "Confirmar código →"}
                    </button>
                  </div>
                )}
              </div>

              <Field
                label="Contraseña"
                type="password"
                value={signupForm.password}
                onChange={(v) => {
                  setSignupForm((f) => ({ ...f, password: v }));
                  setSignupErrors((e) => ({ ...e, password: undefined }));
                }}
                error={signupErrors.password}
                placeholder="Mínimo 8 caracteres"
              />

              <Field
                label="Confirmar contraseña"
                type="password"
                value={signupForm.confirmPassword}
                onChange={(v) => {
                  setSignupForm((f) => ({ ...f, confirmPassword: v }));
                  setSignupErrors((e) => ({
                    ...e,
                    confirmPassword: undefined,
                  }));
                }}
                error={signupErrors.confirmPassword}
                placeholder="Repite tu contraseña"
              />

              {/* CHECKBOXES */}
              <div className="space-y-4 pt-1">
                {/* TERMS — required */}
                <CheckboxField
                  checked={signupForm.isTermsAccepted}
                  onChange={(v) => {
                    setSignupForm((f) => ({ ...f, isTermsAccepted: v }));
                    setSignupErrors((e) => ({
                      ...e,
                      isTermsAccepted: undefined,
                    }));
                  }}
                  error={signupErrors.isTermsAccepted}
                  label={
                    <>
                      Acepto los{" "}
                      <a
                        href="/tickets-store/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300 underline transition"
                      >
                        Términos y Condiciones y la Política de Privacidad
                      </a>{" "}
                      y entiendo que mis datos personales serán tratados para
                      proporcionar acceso a la plataforma Afrodise Music y sus
                      servicios.
                    </>
                  }
                />

                {/* MARKETING AFRODISE — optional */}
                <CheckboxField
                  checked={signupForm.isMarketingConsentAfrodise}
                  onChange={(v) =>
                    setSignupForm((f) => ({
                      ...f,
                      isMarketingConsentAfrodise: v,
                    }))
                  }
                  label="Deseo recibir actualizaciones, anuncios, promociones y ofertas especiales de Afrodise Music sobre eventos, merchandising y otros servicios de la plataforma por email, SMS, WhatsApp u otras comunicaciones electrónicas. Entiendo que puedo retirar mi consentimiento en cualquier momento."
                />

                {/* MARKETING PARTNERS — optional */}
                <CheckboxField
                  checked={signupForm.isMarketingConsentPartners}
                  onChange={(v) =>
                    setSignupForm((f) => ({
                      ...f,
                      isMarketingConsentPartners: v,
                    }))
                  }
                  label="Acepto que Afrodise Music y los socios seleccionados u organizadores de eventos cuyos servicios utilizo puedan enviarme información sobre eventos, productos o servicios relevantes. Puedo darme de baja de estas comunicaciones en cualquier momento."
                />

                {/* LEGAL NOTICE */}
                <div className="bg-[#031c14] border border-green-900/20 rounded-xl px-4 py-3">
                  <p className="text-[10px] text-gray-500 leading-relaxed">
                    Tus datos personales serán tratados de conformidad con el
                    Reglamento General de Protección de Datos (UE) 2016/679
                    (RGPD). Puedes retirar tu consentimiento para comunicaciones
                    de marketing en cualquier momento. Para más información
                    sobre cómo recopilamos, usamos y protegemos tus datos,
                    consulta nuestra{" "}
                    <a
                      href="/tickets-store/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-400/70 hover:text-green-400 underline transition"
                    >
                      Política de Privacidad
                    </a>
                    .
                  </p>
                </div>
              </div>

              {signupApiError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
                  <p className="text-sm text-red-400">{signupApiError}</p>
                </div>
              )}

              <button
                onClick={handleSignup}
                disabled={signupLoading}
                className={`w-full py-4 rounded-xl font-bold tracking-widest uppercase text-sm transition
                  ${
                    signupLoading
                      ? "bg-green-700 text-black/50 cursor-not-allowed"
                      : "bg-green-500 text-black hover:bg-green-400"
                  }`}
              >
                {signupLoading ? "Creando cuenta..." : "Crear cuenta →"}
              </button>

              <p className="text-center text-xs text-gray-500">
                ¿Ya tienes cuenta?{" "}
                <button
                  onClick={() => setMode("login")}
                  className="text-green-400 hover:text-green-300 transition"
                >
                  Iniciar sesión
                </button>
              </p>
            </div>
          )}

          {/* ── SIGNUP SUCCESS ── */}
          {mode === "signup" && signupSuccess && (
            <div className="text-center space-y-5">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
                <span className="text-3xl">✓</span>
              </div>
              <div>
                <p className="text-[10px] tracking-widest text-green-400 uppercase mb-2">
                  Cuenta creada
                </p>
                <h2 className="text-2xl font-bold">¡Bienvenido!</h2>
              </div>
              <button
                onClick={() => setMode("login")}
                className="w-full py-3 rounded-xl border border-green-500 text-green-400 font-bold tracking-widest uppercase text-sm hover:bg-green-500 hover:text-black transition"
              >
                Ir a iniciar sesión
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ── FIELD ── */
function Field({ label, value, onChange, error, placeholder, type = "text" }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] tracking-widest text-gray-400 uppercase">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`bg-[#031c14] border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 outline-none transition
          ${
            error
              ? "border-red-500/60 focus:border-red-500"
              : "border-green-900/40 focus:border-green-500/60"
          }`}
      />
      {error && (
        <p className="text-[10px] text-red-400 tracking-wide">{error}</p>
      )}
    </div>
  );
}

function CheckboxField({ checked, onChange, label, error }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="relative flex-shrink-0 mt-0.5">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="sr-only"
          />
          <div
            className={`w-5 h-5 rounded-md border transition flex items-center justify-center
              ${
                checked
                  ? "bg-green-500 border-green-500"
                  : error
                    ? "bg-[#031c14] border-red-500/60"
                    : "bg-[#031c14] border-green-900/40 group-hover:border-green-500/50"
              }`}
          >
            {checked && (
              <span className="text-black text-xs font-bold leading-none">
                ✓
              </span>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">{label}</p>
      </label>
      {error && (
        <p className="text-[10px] text-red-400 tracking-wide pl-8">{error}</p>
      )}
    </div>
  );
}
